import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './client-info.module.scss';
import { Avatar, Flex, Input, Spin, type InputRef } from 'antd';
import {
  enhancedApi,
  type AgentClientFields,
  type ClientFieldData,
  type DialogShortData,
} from '@shared/api';
import { ClientInfoSkeleton } from './client-info-skeleton';
import { Text } from '@shared/ui/antd';
import {
  ArrowDownOutlinedSmall,
  Avito,
  CopyDetalized,
  CreatorMax,
  CreatorTelegram,
  CreatorVk,
  CreatorWidgetSmall,
  EditDetalized,
} from '@shared/assets/icons/components';
import { copyToClipboard } from '@shared/utils/copy-to-clipboard';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { useTranslation } from 'react-i18next';
import { LeadActions } from './ui/lead-actions/lead-actions';

interface ClientInfoProps {
  selectedDialog: DialogShortData;
}

type EditingFieldState = {
  fieldId: number;
  draft: string;
  initial: string;
};

type DisplayField = {
  key: string;
  label: string;
  value: string;
  agentFieldId: number;
};

type DisplayAgentFields = {
  agentId: number;
  agentName: string;
  currentAgent: boolean;
  fields: DisplayField[];
};

const mapAgentsToDisplay = (agents: AgentClientFields[] = []): DisplayAgentFields[] => {
  return agents.map((agent) => ({
    agentId: agent.agent_id,
    agentName: agent.agent_name,
    currentAgent: agent.current_agent,
    fields: (agent.client_fields ?? []).map((field: ClientFieldData) => ({
      key: `agent_${agent.agent_id}_field_${field.agent_field_id}`,
      label: field.agent_field_name,
      value: field.value ?? '',
      agentFieldId: field.agent_field_id,
    })),
  }));
};

export const ClientInfo: FC<ClientInfoProps> = ({ selectedDialog }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialogs' });
  const { data, isLoading } = enhancedApi.useGetClientFieldsClientFieldsGetQuery({
    clientId: selectedDialog.client_id,
  });

  const [updateClientField, { isLoading: isUpdating }] =
    enhancedApi.useUpdateClientFieldsClientFieldsUpdatePostMutation();

  const [editingField, setEditingField] = useState<EditingFieldState | null>(null);
  const [savingFieldId, setSavingFieldId] = useState<number | null>(null);
  const [expandedAgentIds, setExpandedAgentIds] = useState<Set<number>>(new Set());

  const inputRef = useRef<InputRef>(null);
  const editingRowRef = useRef<HTMLDivElement | null>(null);
  const commitInProgressRef = useRef(false);
  const hasInitializedExpansionRef = useRef(false);

  useEffect(() => {
    setEditingField(null);
    setSavingFieldId(null);
    setExpandedAgentIds(new Set());
    hasInitializedExpansionRef.current = false;
  }, [selectedDialog.client_id]);

  useEffect(() => {
    if (!editingField) return;
    queueMicrotask(() => inputRef.current?.focus());
  }, [editingField]);

  const handleStartEdit = useCallback((agentFieldId: number, value: string) => {
    setEditingField({
      fieldId: agentFieldId,
      draft: value ?? '',
      initial: value ?? '',
    });
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
  }, []);

  const handleCommit = useCallback(async () => {
    if (!editingField) return;
    if (isUpdating) return;
    if (commitInProgressRef.current) return;

    commitInProgressRef.current = true;
    try {
      if (editingField.draft === editingField.initial) {
        setEditingField(null);
        return;
      }

      setSavingFieldId(editingField.fieldId);
      setEditingField(null);

      await updateClientField({
        updateClientFieldData: {
          client_id: selectedDialog.client_id,
          agent_field_id: editingField.fieldId,
          value: editingField.draft,
        },
      }).unwrap();
    } catch {
    } finally {
      commitInProgressRef.current = false;
      setSavingFieldId(null);
    }
  }, [editingField, isUpdating, selectedDialog.client_id, updateClientField]);

  useEffect(() => {
    if (!editingField) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (editingRowRef.current?.contains(target)) return;
      void handleCommit();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelEdit();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingField, handleCancelEdit, handleCommit]);

  const displayFieldsByAgent = useMemo<DisplayAgentFields[]>(() => {
    return mapAgentsToDisplay(data ?? []);
  }, [data]);

  useEffect(() => {
    if (!displayFieldsByAgent.length) {
      setExpandedAgentIds(new Set());
      return;
    }

    setExpandedAgentIds((prev) => {
      const availableIds = new Set(displayFieldsByAgent.map((agent) => agent.agentId));

      if (!hasInitializedExpansionRef.current) {
        hasInitializedExpansionRef.current = true;
        const activeAgentId =
          displayFieldsByAgent.find((agent) => agent.currentAgent)?.agentId ??
          displayFieldsByAgent[0].agentId;

        return new Set([activeAgentId]);
      }

      const preservedIds = new Set<number>();
      prev.forEach((id) => {
        if (availableIds.has(id)) {
          preservedIds.add(id);
        }
      });

      return preservedIds;
    });
  }, [displayFieldsByAgent]);

  const handleToggleAgent = useCallback((agentId: number) => {
    setExpandedAgentIds((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });
  }, []);

  if (isLoading) {
    return <ClientInfoSkeleton />;
  }

  return (
    <Flex align="start" justify="start" className={styles.root} gap={24} vertical>
      <Flex gap={12} align="center">
        <Avatar
          src={selectedDialog?.client_avatar_url}
          size="large"
          shape="circle"
          className={styles.avatar}
        />

        <Flex gap={10} vertical>
          <Flex gap={4} align="center">
            {(selectedDialog.channel_type === 'telegram' ||
              selectedDialog.channel_type === 'telegram_bot') && <CreatorTelegram />}
            {selectedDialog.channel_type === 'widget' && <CreatorWidgetSmall />}
            {selectedDialog.channel_type === 'vk_bot' && <CreatorVk />}
            {selectedDialog.channel_type === 'max_bot' && <CreatorMax width={16} height={16} />}
            {selectedDialog.channel_type === 'avito' && <Avito width={16} height={16} />}

            <Text variant="md" fontWeight={500} hasTooltip maxLength={15}>
              {selectedDialog?.client_name}
            </Text>
          </Flex>

          <Flex gap={8} className={styles.client_id} justify="space-between">
            <Text variant="sm" fontWeight={400}>
              ID {selectedDialog?.client_id}
            </Text>

            <CopyDetalized
              className={styles.copy}
              onClick={() => copyToClipboard(String(selectedDialog?.client_id))}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex gap={12} align="center" vertical className={styles.content}>
        <div className={styles.scrollArea}>
          <ScrollContainer>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <Text variant="sm" fontWeight={500}>
                  {t('clientInfo.leadData')}
                </Text>
                {selectedDialog.lead_created_at ? (
                  <span className={styles.leadTag}>{t('clientInfo.leadCreated')}</span>
                ) : null}
              </div>

              <LeadActions selectedDialog={selectedDialog} />

              <Flex gap={8} vertical className={styles.accordions}>
                {displayFieldsByAgent.map((agent) => {
                  const isExpanded = expandedAgentIds.has(agent.agentId);

                  return (
                    <div key={`agent_${agent.agentId}`} className={styles.accordionItem}>
                      <button
                        type="button"
                        className={styles.accordionButton}
                        aria-expanded={isExpanded}
                        onClick={() => handleToggleAgent(agent.agentId)}
                      >
                        <span className={styles.accordionButtonTitle}>
                          <Text
                            variant="sm"
                            fontWeight={500}
                            hasTooltip
                            maxLength={20}
                            className={styles.accordionAgentName}
                          >
                            {agent.agentName}
                          </Text>

                          {agent.currentAgent ? (
                            <Text variant="xs" fontWeight={400} className={styles.currentAgentTag}>
                              {t('clientInfo.currentAgent')}
                            </Text>
                          ) : null}
                        </span>

                        <ArrowDownOutlinedSmall
                          className={`${styles.accordionArrow} ${
                            isExpanded ? styles.accordionArrowExpanded : ''
                          }`}
                        />
                      </button>

                      {isExpanded ? (
                        <Flex gap={8} vertical className={styles.accordionContent}>
                          {agent.fields.map((field) => {
                            const isEditing = editingField?.fieldId === field.agentFieldId;
                            const isSaving = savingFieldId === field.agentFieldId;

                            return (
                              <Flex
                                key={field.key}
                                vertical
                                gap={4}
                                ref={isEditing ? editingRowRef : undefined}
                              >
                                <Text variant="xs" fontWeight={400} className={styles.fieldLabel}>
                                  {field.label}
                                </Text>

                                <Input
                                  ref={isEditing ? inputRef : undefined}
                                  value={isEditing ? (editingField?.draft ?? '') : field.value}
                                  onFocus={() => {
                                    if (isEditing || isSaving) return;
                                    handleStartEdit(field.agentFieldId, field.value);
                                  }}
                                  onChange={(e) => {
                                    if (!isEditing) return;
                                    setEditingField((prev) => {
                                      if (!prev || prev.fieldId !== field.agentFieldId) return prev;
                                      return {
                                        ...prev,
                                        draft: e.target.value,
                                      };
                                    });
                                  }}
                                  onPressEnter={() => void handleCommit()}
                                  onBlur={() => void handleCommit()}
                                  className={styles.fieldInput}
                                  readOnly={!isEditing}
                                  disabled={isSaving && isUpdating}
                                  suffix={
                                    isSaving && isUpdating ? (
                                      <Spin size="small" />
                                    ) : (
                                      <EditDetalized className={styles.fieldEditIcon} />
                                    )
                                  }
                                />
                              </Flex>
                            );
                          })}
                        </Flex>
                      ) : null}
                    </div>
                  );
                })}
              </Flex>
            </div>
          </ScrollContainer>
        </div>
      </Flex>
    </Flex>
  );
};
